import React, { useEffect, useState, useMemo } from 'react';
import { contractRequestAPI, appointmentAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { PipelineTiles } from '@/components/contracting/PipelineTiles';
import { RecentRequestsStrip } from '@/components/contracting/RecentRequestsStrip';

/**
 * ContractingHealth — the second Command Center band on the KR Dashboard.
 * Sits below AttentionRequired and above the pre-existing widgets.
 *
 * Answers the executive question **"How healthy is the contracting pipeline
 * right now?"** using the SAME 5-stage taxonomy that powers the Agent
 * Command Center, Agency Command Center, /requests, and /kanban (Phase
 * 18.4–18.11 consolidation). No raw status literals; everything routes
 * through `/lib/contracting/statusMapping.js` via the shared primitives
 * `PipelineTiles` and `RecentRequestsStrip`.
 *
 * Data sources (all pre-existing, KR-scoped by the backend):
 *   • GET /contract-requests            — full org-wide requests dataset
 *   • GET /appointments                 — full org-wide appointments dataset
 *
 * Empty states:
 *   • No requests + no appointments after load → "No contracting activity yet"
 *   • Requests exist, but strip is empty (rare edge case) →
 *     "No recent contract activity"
 *
 * Loading state:
 *   • Skeleton hint while both fetches are in flight — prevents empty-state
 *     flash during initial paint.
 */

const RECENT_LIMIT = 5;

const metaFormatter = (r) =>
  [r?.agent_name, r?.contract_type_name].filter(Boolean).join(' · ');

const sortByRecency = (a, b) => {
  const at = new Date(a?.updated_at || a?.created_at || 0).getTime();
  const bt = new Date(b?.updated_at || b?.created_at || 0).getTime();
  return bt - at;
};

export const ContractingHealth = () => {
  const [requests, setRequests] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [reqRes, apptRes] = await Promise.all([
          contractRequestAPI.list(),
          appointmentAPI.list(),
        ]);
        if (cancelled) return;
        setRequests(Array.isArray(reqRes.data) ? reqRes.data : []);
        setAppointments(Array.isArray(apptRes.data) ? apptRes.data : []);
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load contracting health data:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const recentRequests = useMemo(
    () => [...requests].sort(sortByRecency).slice(0, RECENT_LIMIT),
    [requests],
  );

  const isEmpty = !loading && requests.length === 0 && appointments.length === 0;

  return (
    <section className="mb-8" data-testid="contracting-health">
      <div className="mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Contracting Health
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Monitor contract requests, approvals, and active writing numbers
          across KR Enterprise Group.
        </p>
      </div>

      <Card className="animate-slideIn">
        <CardContent className="p-5">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400" data-testid="contracting-health-loading">
              Loading contracting health…
            </div>
          ) : isEmpty ? (
            <div className="py-8 text-center" data-testid="contracting-health-empty">
              <p className="text-sm font-medium text-slate-800">No contracting activity yet</p>
              <p className="text-xs text-slate-500 mt-1">
                Contracting requests and appointments will appear here once activity begins.
              </p>
            </div>
          ) : (
            <>
              <PipelineTiles
                requests={requests}
                appointments={appointments}
                testIdPrefix="kr-pipeline"
              />
              {recentRequests.length === 0 ? (
                <div
                  className="mt-5 pt-4 border-t text-center py-4"
                  style={{ borderColor: 'var(--kr-border)' }}
                  data-testid="contracting-health-no-recent"
                >
                  <p className="text-sm text-slate-500">No recent contract activity</p>
                </div>
              ) : (
                <RecentRequestsStrip
                  requests={recentRequests}
                  maxItems={RECENT_LIMIT}
                  testIdPrefix="kr-recent-request"
                  metaFormatter={metaFormatter}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default ContractingHealth;
